import RestX from 'highered-utils/restx';
import i18n from '~/i18n';

// Custon dialogs
import UniversityVerifyDialog from '~/components/miscellaneous/VerifyModule.Dialog';
import LocalCompanyProfileDialog from '~/views/companydetail/components/LocalProfile.Dialog';
import ArticleDialog from '~/views/companydetail/components/Articles.Dialog';
import VideoDialog from '~/components/miscellaneous/Video.Dialog';
import LocationAllDialog from '~/views/companydetail/components/Locations.All.Dialog';
import EducationDialog from '~/views/spotlight/components/EducationDialog';
import WorkAuthorizationDialog from '~/views/spotlight/components/WorkAuthorizationDialog';
import CuteConsentDialog from '~/views/test_cute/components/ConsentDialog';
import CalendarAboutDialog from '~/views/calendar/overview/components/CalendarAbout.Dialog';
import AppointmentBookDialog from '~/views/calendar/appointment_book/components/AppointmentBook.Dialog';
import AppointmentsDateDialog from '~/views/calendar/overview/components/AppointmentsDate.Dialog';

class DialogService {
    // hasHistory() is a helper method that checks whether dialogModal has registered history that is traversible or not
    static hasHistory() {
        return RestX.getInterfaces().dialogModal.hasHistory();
    }

    static popState() {
        RestX.getInterfaces().dialogModal.popState();
    }

    static setTitle(title) {
        RestX.getInterfaces().dialogModal.setTitle(title);
    }

    static openEducationDialog(props, listeners) {
        RestX.getInterfaces().dialogModal.openModal({
            component: EducationDialog,
            props: props,
            listeners: listeners,
            actions: [],
            clickawayOff: false,
            windowMode: true,
        });
    }

    static openWorkAuthorizationDialog(props, listeners) {
        RestX.getInterfaces().dialogModal.openModal({
            component: WorkAuthorizationDialog,
            props: props,
            listeners: listeners,
            actions: [],
            clickawayOff: false,
            windowMode: true,
        });
    }

    static openAllLocationsDialog(payload) {
        RestX.getInterfaces().dialogModal.openModal({
            component: LocationAllDialog,
            actions: [],
            clickawayOff: true,
            windowMode: true,
            payload: payload,
        });
    }

    static openLocalCompanyProfileDialog(payload) {
        RestX.getInterfaces().dialogModal.openModal({
            component: LocalCompanyProfileDialog,
            actions: [],
            clickawayOff: true,
            // schoolBranding: true,
            windowMode: true,
            payload: payload,
        });
    }

    static openVideoDialog(payload, component) {
        RestX.getInterfaces().dialogModal.openModal({
            component: component || VideoDialog,
            actions: [],
            props: { videoLink: payload.videoLink },
            clickawayOff: true,
            windowMode: true,
            payload: payload,
        });
    }

    static openArticleDialog(payload) {
        RestX.getInterfaces().dialogModal.openModal({
            component: ArticleDialog,
            actions: [],
            props: { videoLink: payload.videoLink },
            clickawayOff: true,
            windowMode: true,
            payload: payload,
        });
    }

    static openUniVerifyDialog(config) {
        RestX.getInterfaces().dialogModal.openModal({
            title: i18n.t('components.miscellaneous.verifyModule.title'),
            component: UniversityVerifyDialog,
            actions: [],
            clickawayOff: false,
            ...config,
        });
    }

    static openCuteConsentDialog(link, listeners) {
        RestX.getInterfaces().dialogModal.openModal({
            component: CuteConsentDialog,
            actions: [],
            props: { link: link },
            listeners: listeners,
            clickawayOff: false,
            windowMode: true,
        });
    }

    static openCalendarAboutDialog() {
        RestX.getInterfaces().dialogModal.openModal({
            component: CalendarAboutDialog,
            actions: [],
            clickawayOff: false,
            windowMode: true,
        });
    }

    static openAppointmentBookingDialog(notification, type) {
        RestX.getInterfaces().dialogModal.openModal({
            title: i18n.t('calendar.bookModal.title'),
            component: AppointmentBookDialog,
            actions: [],
            clickawayOff: false,
            props: {
                ...notification,
                type,
            },
        });
    }

    static openAppointmentsDateDialog(formattedDate) {
        RestX.getInterfaces().dialogModal.openModal({
            title: `${i18n.t(
                'calendar.dateDetailsModal.title'
            )} ${formattedDate}`,
            component: AppointmentsDateDialog,
            actions: [],
            clickawayOff: false,
        });
    }

    static openSpotlightExitDialog(onSaveFunc, routerObject) {
        const redirect = RestX.getInterfaces().unsavedChanges.getRedirect();
        const closeAction = ({ res }) => res('close');
        RestX.getInterfaces().dialogModal.openModal({
            title: i18n.t('spotlight.exitDialog.title'),
            content: i18n.t('spotlight.exitDialog.message'),
            actions: [
                {
                    title: i18n.t('spotlight.exitDialog.stayAction'),
                    variant: 'secondary',
                    action: closeAction,
                },
                {
                    title: i18n.t('spotlight.exitDialog.saveAction'),
                    variant: 'secondary',
                    action: ({ res }) => res('save'),
                },
                {
                    title: i18n.t('spotlight.exitDialog.continueAction'),
                    variant: 'text-secondary',
                    action: ({ rej }) => rej(),
                },
            ],
            onClose: closeAction,

            then: result => {
                RestX.getInterfaces().unsavedChanges.toggleOpen();
                if (result === 'save') {
                    onSaveFunc().then(() => {
                        RestX.getInterfaces().unsavedChanges.setHasUnsavedChanges(
                            false
                        );
                        routerObject.push(redirect);
                    });
                }
            },

            error: () => {
                // console.log('Redirecting to', redirect);
                RestX.getInterfaces().unsavedChanges.setHasUnsavedChanges(
                    false
                );
                RestX.getInterfaces().unsavedChanges.toggleOpen();
                routerObject.push(redirect);
            },
        });
    }

    static openErrorDialog(error) {
        if (!error) {
            return;
        }
        if (error instanceof Array && error.length > 0) {
            error = error[0];
        }

        // Build error message
        let message = `${i18n.t('general.error')}: ${error}`;
        let title = i18n.t('errors.defaultErrorMessage');

        switch (error) {
            case 'INVALID_PROVIDER_LINKEDIN':
                message = i18n.t(
                    'errors.socialAuthErrorText.invalidProviderLinkedIn'
                );
                title = i18n.t('errors.socialAuthErrorText.errorTitle', {
                    provider: 'LinkedIn',
                });
                break;
            case 'INVALID_PROVIDER_FACEBOOK':
                message = i18n.t(
                    'errors.socialAuthErrorText.invalidProviderFacebook'
                );
                title = i18n.t('errors.socialAuthErrorText.errorTitle', {
                    provider: 'Facebook',
                });
                break;
            case 'INVALID_PROVIDER_GOOGLE':
                message = i18n.t(
                    'errors.socialAuthErrorText.invalidProviderGoogle'
                );
                title = i18n.t('errors.socialAuthErrorText.errorTitle', {
                    provider: 'Google',
                });
                break;
            case 'INVALID_PROVIDER_WECHAT':
                message = i18n.t(
                    'errors.socialAuthErrorText.invalidProviderWeChat'
                );
                title = i18n.t('errors.socialAuthErrorText.errorTitle', {
                    provider: 'WeChat',
                });
                break;
        }

        RestX.getInterfaces().dialogModal.openModal({
            title: title,
            content: message,
            isError: true,
            clickawayOff: false,
            actions: [
                {
                    title: i18n.t('general.ok'),
                    variant: 'secondary',
                    action: ({ res }) => res('close'),
                },
            ],
        });
    }
}

export default DialogService;
